import React, {useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';

import {useQuery, useSubscription, useMutation} from '@apollo/client/react';
import {ArrowLeft, Mail, Phone, MapPin, Building2, Calendar, Shield, ShieldAlert, Key, AlertTriangle, Split, X} from 'lucide-react';
import EditUserModal from '../components/EditUserModal';
import {StatusBadge} from '../components/StatusBadge';
import {Avatar} from '../components/Avatar';
import {GraphQLChangeRequestButton} from '../components/GraphQLChangeRequestButton';
import {DateWithRelative} from '../components/DateWithRelative';
import {SplitUserModal} from '../components/SplitUserModal';
import {PhoneNumber} from '../components/PhoneNumber';
import {Toggle} from '../components/Toggle';
import {CopyableId} from '../components/CopyableId';
import {useLoginInfo} from '../graphql/localState';
import {removeMemberDisabledReason} from '../graphql/roles';
import {isFreeEmail} from '../constants/freeEmailDomains';
import {isMultipleEmailsInOne} from '../utils/emailUtils';
import {gql} from 'types/__generated__';

export const GET_USER_QUERY = gql(`
  query GetUser($username: String) {
    getUser(username: $username) {
      id
      username
      email
      firstName
      lastName
      name
      enabled
      acceptedTerms
      createdAt
      isSSO
      earlyAccessProgram
      lightboxEverywhere
      systemAdmin
      avatar
      organisationMemberships {
        organisationId
        name
        isAccountOwner
        role {
          id
          name
          membersChangeableByMe
        }
      }
      address {
        streetAddress
        city
        postalCode
        country
        mobilePhone
        businessPhone
      }
      workInformation {
        companyName
        department
        jobTitle
      }
      loginCity {
        name
        country
        latitude
        longitude
      }
    }
  }
`);

export const USER_SUBSCRIPTION = gql(`
  subscription UserUpdated($userId: UUID!) {
    user(userId: $userId) {
      id
      username
      email
      firstName
      lastName
      name
      enabled
      acceptedTerms
      createdAt
      isSSO
      earlyAccessProgram
      lightboxEverywhere
      systemAdmin
      avatar
      organisationMemberships {
        organisationId
        name
        isAccountOwner
        role {
          id
          name
          membersChangeableByMe
        }
      }
      address {
        streetAddress
        city
        postalCode
        country
        mobilePhone
        businessPhone
      }
      workInformation {
        companyName
        department
        jobTitle
      }
      loginCity {
        name
        country
        latitude
        longitude
      }
    }
  }
`);

export const SET_EARLY_ACCESS = gql(`
  mutation SetEarlyAccess($userId: UUID!, $earlyAccess: Boolean!) {
    setEarlyAccess(userId: $userId, earlyAccess: $earlyAccess)
  }
`);

export const SET_LIGHTBOX_EVERYWHERE = gql(`
  mutation SetLightboxEverywhere($userId: UUID!, $lightboxEverywhere: Boolean!) {
    setLightboxEverywhere(userId: $userId, lightboxEverywhere: $lightboxEverywhere)
  }
`);

export const SET_SYSTEM_ADMIN = gql(`
  mutation SetSystemAdmin($userId: UUID!, $admin: Boolean!) {
    setSystemAdmin(userId: $userId, admin: $admin)
  }
`);

const DELETE_USERS = gql(`
  mutation DeleteUsers($organisationId: UUID, $usernames: [String!]!) {
    deleteUsers(organisationId: $organisationId, usernames: $usernames)
  }
`);

export default function UserDetailPage() {
  const {userId: username} = useParams<{userId: string}>();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSplitModalOpen, setIsSplitModalOpen] = useState(false);
  const [accountUpdateError, setAccountUpdateError] = useState<string | null>(null);
  const [accountUpdateSuccess, setAccountUpdateSuccess] = useState<string | null>(null);
  const [showRemoveOrgConfirm, setShowRemoveOrgConfirm] = useState(false);
  const [orgToRemove, setOrgToRemove] = useState<{organisationId: string; name: string} | null>(null);
  const [pendingSystemAdmin, setPendingSystemAdmin] = useState<boolean | null>(null);

  const loginInfo = useLoginInfo();

  // Query user by username
  const {loading, error, data, refetch} = useQuery(GET_USER_QUERY, {
    variables: {username},
    skip: !username,
  });

  const user = data?.getUser;

  // Subscribe to user updates
  useSubscription(USER_SUBSCRIPTION, {
    variables: {userId: user?.id},
    skip: !user?.id,
    onData: ({data: subscriptionData}) => {
      console.log('[UserDetailPage] Subscription update received:', subscriptionData);
    },
  });

  const [setEarlyAccess, {loading: earlyAccessLoading}] = useMutation(SET_EARLY_ACCESS);
  const [setLightboxEverywhere, {loading: lightboxEverywhereLoading}] = useMutation(SET_LIGHTBOX_EVERYWHERE);
  const [setSystemAdmin, {loading: systemAdminLoading}] = useMutation(SET_SYSTEM_ADMIN);
  const [deleteUsers, {loading: removingFromOrg}] = useMutation(DELETE_USERS);

  const handleEditSuccess = () => {
    // No longer need to refetch - subscription will update cache
  };

  const handleSplitSuccess = () => {
    // Redirect to users list after successful split
    navigate('/admin/users');
  };

  const handleEarlyAccessToggle = async (newValue: boolean) => {
    if (!user) return;

    setAccountUpdateError(null);
    setAccountUpdateSuccess(null);

    try {
      await setEarlyAccess({
        variables: {
          userId: user.id,
          earlyAccess: newValue,
        },
      });
      // Refetch rather than wait on USER_SUBSCRIPTION - see confirmSystemAdminChange.
      await refetch();
      setAccountUpdateSuccess(`Early Access ${newValue ? 'enabled' : 'disabled'} successfully`);
      setTimeout(() => setAccountUpdateSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to update early access:', err);
      setAccountUpdateError(err.message || 'Failed to update early access');
    }
  };

  // setSystemAdmin is system admin only, and the server also rejects it on yourself and
  // from an impersonated session. All three come back as an unhelpful AuthorizationException,
  // so block them in the UI instead. Self-demotion is blocked deliberately: nothing else can
  // set the flag, so the last system admin demoting themselves would be unrecoverable.
  const currentUsername = loginInfo?.user?.username;
  const isImpersonating = !!loginInfo?.impersonatedBy;
  const isSystemAdmin = loginInfo?.isSystemAdmin === true;
  const isSelf = !!currentUsername && !!user && currentUsername.toLowerCase() === user.username.toLowerCase();
  const systemAdminDisabledReason = isImpersonating
    ? 'Not available while impersonating another user'
    : isSelf
      ? 'You cannot change your own system admin role'
      : !isSystemAdmin
        ? 'Only a system administrator can change this'
        : null;

  // setLightboxEverywhere is system admin only and refused from an impersonated session;
  // unlike system admin, setting it on yourself is allowed.
  const lightboxEverywhereDisabledReason = isImpersonating
    ? 'Not available while impersonating another user'
    : !isSystemAdmin
      ? 'Only a system administrator can change this'
      : null;

  const handleLightboxEverywhereToggle = async (newValue: boolean) => {
    if (!user || lightboxEverywhereDisabledReason) return;

    setAccountUpdateError(null);
    setAccountUpdateSuccess(null);

    try {
      await setLightboxEverywhere({variables: {userId: user.id, lightboxEverywhere: newValue}});
      // Refetch rather than wait on USER_SUBSCRIPTION - see confirmSystemAdminChange.
      await refetch();
      setAccountUpdateSuccess(newValue ? 'Lightbox granted in every organisation' : 'Lightbox everywhere revoked');
      setTimeout(() => setAccountUpdateSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to update Lightbox everywhere:', err);
      setAccountUpdateError(err.message || 'Could not change Lightbox everywhere');
    }
  };

  const handleSystemAdminToggle = (newValue: boolean) => {
    if (!user || systemAdminDisabledReason) return;
    setAccountUpdateError(null);
    setAccountUpdateSuccess(null);
    setPendingSystemAdmin(newValue);
  };

  const confirmSystemAdminChange = async () => {
    if (!user || pendingSystemAdmin === null) return;

    const newValue = pendingSystemAdmin;
    setAccountUpdateError(null);
    setAccountUpdateSuccess(null);

    try {
      // The mutation returns true or throws - failures arrive as a GraphQL error, never as `false`.
      await setSystemAdmin({
        variables: {
          userId: user.id,
          admin: newValue,
        },
      });
      setPendingSystemAdmin(null);
      // USER_SUBSCRIPTION cannot refresh this for us: the server resolves the subscription
      // payload on a publisher thread with no security context, so the org-scoped field it
      // selects (organisationMemberships) throws AuthorizationException. It is non-null in the
      // schema, so the error propagates and the whole `user` arrives as null, and Apollo writes
      // nothing to the cache. Refetch explicitly, as removing from an organisation already does.
      await refetch();
      setAccountUpdateSuccess(newValue ? 'Granted system admin' : 'Revoked system admin');
      setTimeout(() => setAccountUpdateSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to update system admin:', err);
      setPendingSystemAdmin(null);
      setAccountUpdateError(err.message || 'Could not change system admin');
    }
  };

  const cancelSystemAdminChange = () => {
    setPendingSystemAdmin(null);
  };

  const handleAccountStatusToggle = (field: 'enabled' | 'acceptedTerms') => {
    // TODO: Implement backend mutations for these fields
    setAccountUpdateError(`Toggling ${field} is not yet implemented. Backend mutation required.`);
    setTimeout(() => setAccountUpdateError(null), 5000);
  };

  const handleAuthMethodChange = (newMethod: string) => {
    // TODO: Implement backend mutation for changing auth method
    setAccountUpdateError('Changing authentication method is not yet implemented. Backend mutation required.');
    setTimeout(() => setAccountUpdateError(null), 5000);
  };

  const handleRemoveFromOrgClick = (organisationId: string, name: string) => {
    setOrgToRemove({organisationId, name});
    setShowRemoveOrgConfirm(true);
  };

  const confirmRemoveFromOrg = async () => {
    if (!orgToRemove || !user) return;

    try {
      await deleteUsers({
        variables: {
          organisationId: orgToRemove.organisationId,
          usernames: [user.username],
        },
      });
      setShowRemoveOrgConfirm(false);
      setOrgToRemove(null);
      setAccountUpdateSuccess(`Removed from ${orgToRemove.name}`);
      setTimeout(() => setAccountUpdateSuccess(null), 3000);
      // Refetch to update the organization memberships
      await refetch();
    } catch (err: any) {
      console.error('Error removing user from organisation:', err);
      setAccountUpdateError(err.message || 'Failed to remove from organisation');
      setShowRemoveOrgConfirm(false);
      setOrgToRemove(null);
    }
  };

  const cancelRemoveFromOrg = () => {
    setShowRemoveOrgConfirm(false);
    setOrgToRemove(null);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h3 className="text-red-800 dark:text-red-200 font-semibold">Error loading user</h3>
          <p className="text-red-600 dark:text-red-300 mt-1">{error.message}</p>
          <button onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate('/admin/users')} className="mt-4 text-blue-600 dark:text-blue-400 hover:underline">
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">User not found</h1>
          <button onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate('/admin/users')} className="mt-4 text-blue-600 dark:text-blue-400 hover:underline">
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Malformed Email Warning Banner */}
      {isMultipleEmailsInOne(user.email) && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" size={24} />
            <div className="flex-1">
              <h3 className="text-orange-800 dark:text-orange-200 font-semibold">Malformed User Account Detected</h3>
              <p className="text-orange-700 dark:text-orange-300 mt-1 text-sm">
                This user account contains multiple comma-separated email addresses. This is a data integrity issue. Click the "Split User" button to
                separate these into individual user accounts.
              </p>
            </div>
            <button
              onClick={() => setIsSplitModalOpen(true)}
              className="flex items-center space-x-2 px-4 py-2 text-orange-600 dark:text-orange-400 bg-white dark:bg-orange-900/30 hover:bg-orange-100 dark:hover:bg-orange-900/40 rounded-lg transition-colors border border-orange-300 dark:border-orange-700 whitespace-nowrap"
            >
              <Split size={18} />
              <span className="text-sm font-medium">Split User</span>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate('/admin/users')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">User Details</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">View and manage user information</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Status Badge - Toggle not yet available (needs backend mutation) */}
          <StatusBadge type="user" enabled={user.enabled} acceptedTerms={user.acceptedTerms} size="lg" />
          <GraphQLChangeRequestButton />
          {isMultipleEmailsInOne(user.email) && (
            <button
              onClick={() => setIsSplitModalOpen(true)}
              className="flex items-center space-x-2 px-3 py-2 text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-lg transition-colors border border-orange-200 dark:border-orange-800"
              title="Split malformed user account"
            >
              <Split size={18} />
              <span className="text-sm font-medium">Split User</span>
            </button>
          )}
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            title="Edit user"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* User Profile Card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-blue-700 h-32"></div>
        <div className="px-6 pb-6">
          <div className="flex items-end -mt-16 mb-4">
            <Avatar firstName={user.firstName} lastName={user.lastName} size="xl" className="border-4 border-white dark:border-gray-800 shadow-lg" />
            <div className="ml-6 mb-2">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100" title="Property: name (computed from firstName + lastName)">
                {user.name || `${user.firstName} ${user.lastName}` || 'No name set'}
              </h2>
              <div className="flex items-center gap-2">
                <p
                  className={`${
                    isMultipleEmailsInOne(user.email)
                      ? 'text-orange-600 dark:text-orange-400 font-semibold'
                      : isFreeEmail(user.email)
                        ? 'text-red-600 dark:text-red-400 font-medium'
                        : 'text-gray-600 dark:text-gray-400'
                  }`}
                  title="Property: email"
                >
                  {user.email}
                </p>
                {isMultipleEmailsInOne(user.email) && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs rounded-full">
                    <Split size={12} />
                    <span>Malformed - Multiple emails</span>
                  </div>
                )}
                {isFreeEmail(user.email) && !isMultipleEmailsInOne(user.email) && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded-full">
                    <AlertTriangle size={12} />
                    <span>Free email provider</span>
                  </div>
                )}
              </div>
              <div className="mt-1" title="Property: id">
                <CopyableId value={user.id} label="User ID:" title="Copy user ID" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {user.address.mobilePhone && (
              <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-400">
                <Phone size={20} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: address.mobilePhone">
                    Mobile Phone
                  </p>
                  <PhoneNumber phoneNumber={user.address.mobilePhone} size="md" />
                </div>
              </div>
            )}
            {user.address.businessPhone && (
              <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-400">
                <Phone size={20} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: address.businessPhone">
                    Business Phone
                  </p>
                  <PhoneNumber phoneNumber={user.address.businessPhone} size="md" />
                </div>
              </div>
            )}
            {(user.address.city || user.address.country) && (
              <div className="flex items-center space-x-3 text-gray-600 dark:text-gray-400">
                <MapPin size={20} />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: address.city, address.country">
                    Location
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: address.city, address.country">
                    {[user.address.city, user.address.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Work Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <Building2 size={20} className="mr-2" />
            Work Information
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: workInformation.companyName">
                Company
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: workInformation.companyName">
                {user.workInformation.companyName || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: workInformation.department">
                Department
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: workInformation.department">
                {user.workInformation.department || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: workInformation.jobTitle">
                Job Title
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: workInformation.jobTitle">
                {user.workInformation.jobTitle || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Account Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <Shield size={20} className="mr-2" />
            Account Information
          </h3>

          {/* Success/Error Messages */}
          {accountUpdateError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200">{accountUpdateError}</p>
            </div>
          )}
          {accountUpdateSuccess && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-200">{accountUpdateSuccess}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* Account Enabled Toggle */}
            <Toggle
              label="Account Enabled"
              description="Enable or disable user account"
              checked={user.enabled}
              onChange={() => handleAccountStatusToggle('enabled')}
              disabled={true}
            />

            {/* Terms Accepted Toggle */}
            <Toggle
              label="Terms Accepted"
              description="Whether user has accepted terms of service"
              checked={user.acceptedTerms}
              onChange={() => handleAccountStatusToggle('acceptedTerms')}
              disabled={true}
            />

            {/* Early Access Toggle */}
            <Toggle
              label="Early Access Program"
              description="Enroll user in early access features"
              checked={user.earlyAccessProgram}
              onChange={handleEarlyAccessToggle}
              disabled={earlyAccessLoading}
            />

            {/* Lightbox Everywhere Toggle */}
            <Toggle
              label="Lightbox Everywhere"
              description={
                lightboxEverywhereDisabledReason ??
                (user.systemAdmin
                  ? 'System administrators already have Lightbox in every organisation'
                  : 'Lightbox in every organisation without a Lightbox seat, e.g. Collabra staff working as a supplier')
              }
              checked={user.lightboxEverywhere}
              onChange={handleLightboxEverywhereToggle}
              disabled={lightboxEverywhereLoading || !!lightboxEverywhereDisabledReason}
            />

            {/* System Admin Toggle */}
            <Toggle
              label="System Administrator"
              description={systemAdminDisabledReason ?? 'Grants full access to every organisation and all customer data'}
              checked={user.systemAdmin}
              onChange={handleSystemAdminToggle}
              disabled={systemAdminLoading || !!systemAdminDisabledReason}
            />

            {/* Authentication Method Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Authentication Method</label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">How the user authenticates</p>
              <select
                value={user.isSSO ? 'sso' : 'password'}
                onChange={(e) => handleAuthMethodChange(e.target.value)}
                disabled={true}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="password">Password Authentication</option>
                <option value="sso">Single Sign-On (SSO)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Activity */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <Calendar size={20} className="mr-2" />
            Activity
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help mb-2" title="Property: createdAt">
                Account Created
              </p>
              {user.createdAt ? (
                <DateWithRelative timestamp={user.createdAt} showIcon={false} />
              ) : (
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Never</p>
              )}
            </div>
            {/* Note: lastLogin requires organization context and is not available in general user query */}
          </div>
        </div>

        {/* Organizations */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <Key size={20} className="mr-2" />
            Organization Memberships
          </h3>
          {user.organisationMemberships && user.organisationMemberships.length > 0 ? (
            <div className="space-y-3">
              {user.organisationMemberships.map((org, index) => (
                <div key={org.organisationId || index} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p
                        className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help"
                        title="Property: organisationMemberships[].name"
                      >
                        {org.name}
                      </p>
                      <p
                        className="text-xs text-gray-500 dark:text-gray-400 mt-1 cursor-help"
                        title="Property: organisationMemberships[].organisationId"
                      >
                        ID: {org.organisationId}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span
                          className="inline-block px-2 py-1 text-xs font-medium rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 cursor-help"
                          title="Property: organisationMemberships[].role.name"
                        >
                          {org.role.name}
                        </span>
                        {org.isAccountOwner && (
                          <span
                            className="inline-block px-2 py-1 text-xs font-medium rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 cursor-help"
                            title="Property: organisationMemberships[].isAccountOwner"
                          >
                            Account owner
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveFromOrgClick(org.organisationId, org.name)}
                      disabled={!!removeMemberDisabledReason(org, isSystemAdmin)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                      title={removeMemberDisabledReason(org, isSystemAdmin) ?? 'Remove from organisation'}
                      aria-label={`Remove from ${org.name}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">No organization memberships</p>
            </div>
          )}
        </div>
      </div>

      {/* Login City Information */}
      {user.loginCity && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <MapPin size={20} className="mr-2" />
            Last Login Location
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: loginCity.name">
                City
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: loginCity.name">
                {user.loginCity.name}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: loginCity.country">
                Country
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: loginCity.country">
                {user.loginCity.country}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: loginCity.latitude">
                Latitude
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: loginCity.latitude">
                {user.loginCity.latitude}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: loginCity.longitude">
                Longitude
              </p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: loginCity.longitude">
                {user.loginCity.longitude}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Address Information */}
      {(user.address?.streetAddress || user.address?.postalCode) && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
            <MapPin size={20} className="mr-2" />
            Address
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {user.address.streetAddress && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: address.streetAddress">
                  Street Address
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: address.streetAddress">
                  {user.address.streetAddress}
                </p>
              </div>
            )}
            {user.address.postalCode && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: address.postalCode">
                  Postal Code
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: address.postalCode">
                  {user.address.postalCode}
                </p>
              </div>
            )}
            {user.address.city && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: address.city">
                  City
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: address.city">
                  {user.address.city}
                </p>
              </div>
            )}
            {user.address.country && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-500 cursor-help" title="Property: address.country">
                  Country
                </p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 cursor-help" title="Property: address.country">
                  {user.address.country}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* System Admin Confirmation Modal */}
      {pendingSystemAdmin !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start space-x-3 mb-2">
              <ShieldAlert className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={24} />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {pendingSystemAdmin ? 'Grant System Administrator' : 'Revoke System Administrator'}
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {pendingSystemAdmin ? (
                <>
                  Are you sure you want to make <strong>{user.name || user.username}</strong> a system administrator?
                  <span className="block mt-2 text-red-600 dark:text-red-400">
                    This grants cross-organisation access to every customer's data. It takes effect immediately, without the user logging in again.
                  </span>
                </>
              ) : (
                <>
                  Are you sure you want to revoke system administrator from <strong>{user.name || user.username}</strong>?
                  <span className="block mt-2 text-orange-600 dark:text-orange-400">
                    They will immediately lose cross-organisation access. Only another system administrator can grant it back.
                  </span>
                </>
              )}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelSystemAdminChange}
                disabled={systemAdminLoading}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmSystemAdminChange}
                disabled={systemAdminLoading}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {systemAdminLoading ? 'Saving...' : pendingSystemAdmin ? 'Grant system admin' : 'Revoke system admin'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove from Organisation Confirmation Modal */}
      {showRemoveOrgConfirm && orgToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Remove from Organisation</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to remove <strong>{user.name || user.username}</strong> from <strong>{orgToRemove.name}</strong>?
              <span className="block mt-2 text-orange-600 dark:text-orange-400">
                This will revoke their access to this organisation and all its projects.
              </span>
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelRemoveFromOrg}
                disabled={removingFromOrg}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveFromOrg}
                disabled={removingFromOrg}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {removingFromOrg ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      <EditUserModal user={user} isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSuccess={handleEditSuccess} />

      {/* Split User Modal */}
      {user && <SplitUserModal user={user} isOpen={isSplitModalOpen} onClose={() => setIsSplitModalOpen(false)} onSuccess={handleSplitSuccess} />}
    </div>
  );
}
